import { ReactNode, useRef, useState, useEffect } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
}

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const engaged = useRef(false); // True once we've committed to pull-to-refresh
  const wasScrolled = useRef(false); // Track if container was scrolled during this touch

  const threshold = 80; // Distance in pixels to trigger refresh
  const maxPull = 120; // Maximum pull distance
  const engageThreshold = 20; // Minimum downward px before engaging pull-to-refresh

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getScrollTop = () => {
      // Check both the container and window scroll position
      return Math.max(container.scrollTop, window.scrollY, document.documentElement.scrollTop);
    };

    const handleTouchStart = (e: TouchEvent) => {
      engaged.current = false;
      const scrollTop = getScrollTop();
      // Track whether the user started the touch while scrolled down
      wasScrolled.current = scrollTop > 0;
      // Only allow pull-to-refresh if already at the very top
      if (scrollTop === 0) {
        startY.current = e.touches[0].clientY;
      } else {
        startY.current = 0;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (refreshing || startY.current === 0) return;

      const scrollTop = getScrollTop();

      // If the page scrolled at any point during this gesture, abort
      if (scrollTop > 0) {
        startY.current = 0;
        engaged.current = false;
        if (pulling) {
          setPulling(false);
          setPullDistance(0);
        }
        return;
      }

      const currentY = e.touches[0].clientY;
      const distance = currentY - startY.current;

      // Only track downward pulls when at top
      if (distance > 0 && scrollTop === 0) {
        // Require a deliberate downward pull before engaging
        if (!engaged.current) {
          if (distance >= engageThreshold) {
            engaged.current = true;
          } else {
            return; // Not yet committed - let normal scroll behavior happen
          }
        }

        setPulling(true);
        // Apply diminishing returns to pull distance (subtract engage threshold)
        const effectiveDistance = distance - engageThreshold;
        const dampened = Math.min(effectiveDistance * 0.5, maxPull);
        setPullDistance(dampened);

        // Prevent default scroll behavior during pull
        e.preventDefault();
      } else if (distance < 0) {
        // User is scrolling up (finger moving up) — cancel any pull tracking
        startY.current = 0;
        engaged.current = false;
      }
    };

    const handleTouchEnd = async () => {
      if (!pulling) {
        startY.current = 0;
        engaged.current = false;
        return;
      }

      if (pullDistance >= threshold) {
        setRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
        }
      }

      setPulling(false);
      setPullDistance(0);
      startY.current = 0;
      engaged.current = false;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pulling, pullDistance, refreshing, onRefresh]);

  const showIndicator = pulling || refreshing;
  const indicatorOpacity = Math.min(pullDistance / threshold, 1);
  const spinnerRotation = refreshing ? 'animate-spin' : '';

  return (
    <div ref={containerRef} className="overflow-auto h-full">
      {/* Pull indicator */}
      {showIndicator && (
        <div
          className="flex justify-center items-center py-4 transition-all"
          style={{
            transform: `translateY(${pulling ? pullDistance - 40 : 0}px)`,
            opacity: refreshing ? 1 : indicatorOpacity,
          }}
        >
          <div
            className={`w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full ${spinnerRotation}`}
          />
        </div>
      )}

      {/* Content */}
      <div
        style={{
          transform: `translateY(${pulling && !refreshing ? pullDistance : 0}px)`,
          transition: pulling ? 'none' : 'transform 0.3s ease-out',
        }}
      >
        {children}
      </div>
    </div>
  );
}
